// ═══════════════════════════════════════════════════════════════════════════
// GESTION SEMENCES - LA CAVERNE D'ALI BABA 🌱
// ═══════════════════════════════════════════════════════════════════════════
// Farm-Booking - Papy 73+ - Joshua Edition 🎮
// Interface complète : Stock, Alertes, Mouvements, Fournisseurs
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import {
  Package, AlertTriangle, Plus, Search, Filter, RefreshCw,
  Leaf, Calendar, Euro, TrendingDown, TrendingUp, Clock,
  ChevronDown, ChevronUp, X, Edit, Trash2, Eye, Archive,
  Thermometer, Droplets, ShoppingCart, Truck, AlertCircle,
  Check, XCircle, History, BarChart3
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT : Badge Statut Stock
// ═══════════════════════════════════════════════════════════════════════════
const BadgeStatut = ({ statut, type = 'stock' }) => {
  const configs = {
    // Statuts stock
    ok: { bg: 'bg-green-100', text: 'text-green-800', label: '✅ OK' },
    stock_bas: { bg: 'bg-orange-100', text: 'text-orange-800', label: '⚠️ Stock bas' },
    epuise: { bg: 'bg-red-100', text: 'text-red-800', label: '❌ Épuisé' },
    // Statuts péremption
    perime: { bg: 'bg-red-100', text: 'text-red-800', label: '💀 Périmé' },
    peremption_proche: { bg: 'bg-orange-100', text: 'text-orange-800', label: '⏰ Péremption proche' },
    inconnu: { bg: 'bg-gray-100', text: 'text-gray-600', label: '❓ Inconnu' },
  };
  
  const config = configs[statut] || configs.inconnu;
  
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT : Carte Lot Semences
// ═══════════════════════════════════════════════════════════════════════════
const CarteLot = ({ lot, onEdit, onVoirDetail, onMouvement }) => {
  const [expanded, setExpanded] = useState(false);
  
  const pctRestant = lot.quantite_initiale > 0 
    ? (lot.quantite_restante / lot.quantite_initiale * 100) 
    : 0;
  
  return (
    <div className={`bg-white rounded-xl shadow-md hover:shadow-lg transition-all border-l-4 ${
      lot.statut_stock === 'epuise' ? 'border-red-500' :
      lot.statut_stock === 'stock_bas' ? 'border-orange-500' :
      lot.statut_peremption === 'perime' ? 'border-gray-500' :
      lot.statut_peremption === 'peremption_proche' ? 'border-yellow-500' :
      'border-green-500'
    }`}>
      {/* Header */}
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{lot.icone || '🌱'}</span>
            <div>
              <h3 className="font-bold text-gray-900">{lot.variete_nom}</h3>
              <p className="text-sm text-gray-500">{lot.code_lot}</p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <BadgeStatut statut={lot.statut_stock} />
            {lot.statut_peremption !== 'ok' && (
              <BadgeStatut statut={lot.statut_peremption} />
            )}
          </div>
        </div>
        
        {/* Barre de stock */}
        <div className="mt-4">
          <div className="flex justify-between text-sm mb-1">
            <span className="text-gray-600">Stock restant</span>
            <span className="font-bold">
              {lot.quantite_restante} / {lot.quantite_initiale} {lot.unite}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className={`h-3 rounded-full transition-all ${
                pctRestant <= 20 ? 'bg-red-500' :
                pctRestant <= 50 ? 'bg-orange-500' :
                'bg-green-500'
              }`}
              style={{ width: `${Math.min(100, pctRestant)}%` }}
            />
          </div>
        </div>
        
        {/* Infos rapides */}
        <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
          <div className="flex items-center gap-1 text-gray-600">
            <Truck className="w-4 h-4" />
            <span className="truncate">{lot.fournisseur_nom || 'Non spécifié'}</span>
          </div>
          <div className="flex items-center gap-1 text-gray-600">
            <Calendar className="w-4 h-4" />
            <span>{lot.date_peremption ? new Date(lot.date_peremption).toLocaleDateString('fr-FR') : 'N/A'}</span>
          </div>
          {lot.taux_germination_effectif && (
            <div className="flex items-center gap-1 text-gray-600">
              <Leaf className="w-4 h-4" />
              <span>Germi: {lot.taux_germination_effectif}%</span>
            </div>
          )}
          {lot.prix_achat && (
            <div className="flex items-center gap-1 text-gray-600">
              <Euro className="w-4 h-4" />
              <span>{parseFloat(lot.prix_achat).toFixed(2)}€</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Actions */}
      <div className="border-t bg-gray-50 p-2 flex justify-between items-center rounded-b-xl">
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          Détails
        </button>
        <div className="flex gap-1">
          <button
            onClick={() => onMouvement(lot, 'sortie_semis')}
            className="p-1.5 bg-green-100 text-green-700 rounded hover:bg-green-200"
            title="Enregistrer semis"
          >
            <TrendingDown className="w-4 h-4" />
          </button>
          <button
            onClick={() => onEdit(lot)}
            className="p-1.5 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
            title="Modifier"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={() => onVoirDetail(lot)}
            className="p-1.5 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
            title="Voir historique"
          >
            <History className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {/* Détails dépliables */}
      {expanded && (
        <div className="border-t p-4 bg-gray-50 text-sm space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div><span className="text-gray-500">Famille:</span> {lot.famille_nom}</div>
            <div><span className="text-gray-500">Réf. fourn:</span> {lot.reference_fournisseur || 'N/A'}</div>
            <div><span className="text-gray-500">Date achat:</span> {lot.date_achat ? new Date(lot.date_achat).toLocaleDateString('fr-FR') : 'N/A'}</div>
            <div><span className="text-gray-500">Bio:</span> {lot.bio === 'oui' ? '✅ Oui' : '❌ Non'}</div>
            <div><span className="text-gray-500">Certification:</span> {lot.certification || 'N/A'}</div>
            <div><span className="text-gray-500">Lieu stockage:</span> {lot.lieu_stockage || 'N/A'}</div>
          </div>
          {lot.notes && (
            <div className="pt-2 border-t">
              <span className="text-gray-500">Notes:</span> {lot.notes}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT : Modal Nouveau Lot / Édition
// ═══════════════════════════════════════════════════════════════════════════
const ModalLot = ({ lot, varietes, fournisseurs, onSave, onClose }) => {
  const [form, setForm] = useState(lot || {
    variete_id: '',
    fournisseur_id: '',
    fournisseur: '',
    type_semence: 'graine',
    reference_fournisseur: '',
    date_achat: new Date().toISOString().split('T')[0],
    quantite_initiale: '',
    unite: 'graines',
    taux_germination_annonce: '',
    date_peremption: '',
    bio: 'oui',
    certification: '',
    lieu_stockage: '',
    prix_achat: '',
    alerte_stock_bas: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      alert('Erreur: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-auto shadow-2xl">
        <div className="sticky top-0 bg-gradient-to-r from-green-600 to-emerald-600 p-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Package className="w-6 h-6" />
            {lot ? 'Modifier le lot' : '🌱 Nouveau lot de semences'}
          </h2>
          <button onClick={onClose} className="text-white hover:bg-white/20 p-1 rounded">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Variété */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Variété *</label>
            <select
              value={form.variete_id}
              onChange={(e) => setForm({...form, variete_id: e.target.value})}
              required
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-green-500"
            >
              <option value="">-- Sélectionner --</option>
              {varietes.map(v => (
                <option key={v.id} value={v.id}>{v.icone} {v.nom_complet || `${v.espece} ${v.variete || ''}`}</option>
              ))}
            </select>
          </div>
          
          {/* Fournisseur */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fournisseur</label>
              <select
                value={form.fournisseur_id || ''}
                onChange={(e) => setForm({...form, fournisseur_id: e.target.value})}
                className="w-full p-2 border rounded-lg"
              >
                <option value="">-- Liste --</option>
                {fournisseurs.map(f => (
                  <option key={f.id} value={f.id}>{f.nom}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ou saisir</label>
              <input
                type="text"
                value={form.fournisseur || ''}
                onChange={(e) => setForm({...form, fournisseur: e.target.value})}
                placeholder="Nom fournisseur..."
                className="w-full p-2 border rounded-lg"
              />
            </div>
          </div>
          
          {/* Type & Ref */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={form.type_semence}
                onChange={(e) => setForm({...form, type_semence: e.target.value})}
                className="w-full p-2 border rounded-lg"
              >
                <option value="graine">🌱 Graine</option>
                <option value="tubercule">🥔 Tubercule</option>
                <option value="bulbe">🧅 Bulbe</option>
                <option value="plant">🌿 Plant</option>
                <option value="bouture">✂️ Bouture</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Réf. fournisseur</label>
              <input
                type="text"
                value={form.reference_fournisseur || ''}
                onChange={(e) => setForm({...form, reference_fournisseur: e.target.value})}
                placeholder="REF-123..."
                className="w-full p-2 border rounded-lg"
              />
            </div>
          </div>
          
          {/* Quantité */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Quantité *</label>
              <input
                type="number"
                value={form.quantite_initiale}
                onChange={(e) => setForm({...form, quantite_initiale: e.target.value})}
                required
                min="0"
                step="0.01"
                className="w-full p-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Unité</label>
              <select
                value={form.unite}
                onChange={(e) => setForm({...form, unite: e.target.value})}
                className="w-full p-2 border rounded-lg"
              >
                <option value="graines">Graines</option>
                <option value="grammes">Grammes</option>
                <option value="kg">Kg</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Alerte stock bas</label>
              <input
                type="number"
                value={form.alerte_stock_bas || ''}
                onChange={(e) => setForm({...form, alerte_stock_bas: e.target.value})}
                placeholder="Seuil..."
                min="0"
                className="w-full p-2 border rounded-lg"
              />
            </div>
          </div>
          
          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date achat</label>
              <input
                type="date"
                value={form.date_achat || ''}
                onChange={(e) => setForm({...form, date_achat: e.target.value})}
                className="w-full p-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date péremption</label>
              <input
                type="date"
                value={form.date_peremption || ''}
                onChange={(e) => setForm({...form, date_peremption: e.target.value})}
                className="w-full p-2 border rounded-lg"
              />
            </div>
          </div>
          
          {/* Germination & Prix */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Taux germination (%)</label>
              <input
                type="number"
                value={form.taux_germination_annonce || ''}
                onChange={(e) => setForm({...form, taux_germination_annonce: e.target.value})}
                min="0"
                max="100"
                placeholder="85..."
                className="w-full p-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prix achat (€)</label>
              <input
                type="number"
                value={form.prix_achat || ''}
                onChange={(e) => setForm({...form, prix_achat: e.target.value})}
                min="0"
                step="0.01"
                placeholder="4.50..."
                className="w-full p-2 border rounded-lg"
              />
            </div>
          </div>
          
          {/* Bio & Certification */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
              <select
                value={form.bio}
                onChange={(e) => setForm({...form, bio: e.target.value})}
                className="w-full p-2 border rounded-lg"
              >
                <option value="oui">✅ Oui</option>
                <option value="non">❌ Non</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Certification</label>
              <input
                type="text"
                value={form.certification || ''}
                onChange={(e) => setForm({...form, certification: e.target.value})}
                placeholder="AB, Demeter..."
                className="w-full p-2 border rounded-lg"
              />
            </div>
          </div>
          
          {/* Lieu stockage */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Lieu de stockage</label>
            <input
              type="text"
              value={form.lieu_stockage || ''}
              onChange={(e) => setForm({...form, lieu_stockage: e.target.value})}
              placeholder="Armoire serre, Frigo semences..."
              className="w-full p-2 border rounded-lg"
            />
          </div>
          
          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={form.notes || ''}
              onChange={(e) => setForm({...form, notes: e.target.value})}
              rows={2}
              placeholder="Observations..."
              className="w-full p-2 border rounded-lg"
            />
          </div>
          
          {/* Boutons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {lot ? 'Enregistrer' : 'Créer le lot'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT : Modal Mouvement Stock
// ═══════════════════════════════════════════════════════════════════════════
const ModalMouvement = ({ lot, type, onSave, onClose }) => {
  const [form, setForm] = useState({
    type_mouvement: type || 'sortie_semis',
    quantite: '',
    motif: '',
    destinataire: ''
  });
  const [saving, setSaving] = useState(false);

  const typesMouvement = [
    { value: 'sortie_semis', label: '🌱 Sortie semis', sens: 'sortie' },
    { value: 'sortie_don', label: '🎁 Don/Échange', sens: 'sortie' },
    { value: 'sortie_perime', label: '🗑️ Périmé/Jeté', sens: 'sortie' },
    { value: 'sortie_perte', label: '❌ Perte', sens: 'sortie' },
    { value: 'entree_recolte', label: '🌾 Récolte graines', sens: 'entree' },
    { value: 'entree_don', label: '🎁 Reçu don', sens: 'entree' },
    { value: 'entree_correction', label: '📝 Correction +', sens: 'entree' },
    { value: 'sortie_correction', label: '📝 Correction -', sens: 'sortie' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({
        lot_semence_id: lot.lot_id,
        ...form,
        unite: lot.unite
      });
      onClose();
    } catch (err) {
      alert('Erreur: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-4 flex justify-between items-center rounded-t-xl">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <TrendingDown className="w-5 h-5" />
            Mouvement de stock
          </h2>
          <button onClick={onClose} className="text-white hover:bg-white/20 p-1 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Info lot */}
          <div className="bg-gray-50 p-3 rounded-lg flex items-center gap-3">
            <span className="text-2xl">{lot.icone || '🌱'}</span>
            <div>
              <div className="font-medium">{lot.variete_nom}</div>
              <div className="text-sm text-gray-500">
                Stock actuel: {lot.quantite_restante} {lot.unite}
              </div>
            </div>
          </div>
          
          {/* Type mouvement */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={form.type_mouvement}
              onChange={(e) => setForm({...form, type_mouvement: e.target.value})}
              className="w-full p-2 border rounded-lg"
            >
              {typesMouvement.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          
          {/* Quantité */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Quantité ({lot.unite})</label>
            <input
              type="number"
              value={form.quantite}
              onChange={(e) => setForm({...form, quantite: e.target.value})}
              required
              min="0.01"
              step="0.01"
              max={form.type_mouvement.startsWith('sortie') ? lot.quantite_restante : undefined}
              className="w-full p-2 border rounded-lg"
            />
          </div>
          
          {/* Destinataire (si don) */}
          {form.type_mouvement.includes('don') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Destinataire</label>
              <input
                type="text"
                value={form.destinataire || ''}
                onChange={(e) => setForm({...form, destinataire: e.target.value})}
                placeholder="Nom..."
                className="w-full p-2 border rounded-lg"
              />
            </div>
          )}
          
          {/* Motif */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Motif / Notes</label>
            <input
              type="text"
              value={form.motif || ''}
              onChange={(e) => setForm({...form, motif: e.target.value})}
              placeholder="Semis tomates serre..."
              className="w-full p-2 border rounded-lg"
            />
          </div>
          
          {/* Boutons */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
            >
              {saving ? 'Enregistrement...' : 'Valider'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT : Panneau Alertes
// ═══════════════════════════════════════════════════════════════════════════
const PanneauAlertes = ({ alertes, onRefresh }) => {
  if (!alertes || alertes.length === 0) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
        <Check className="w-8 h-8 text-green-500 mx-auto mb-2" />
        <p className="text-green-700 font-medium">Aucune alerte</p>
        <p className="text-green-600 text-sm">Tout va bien dans la caverne !</p>
      </div>
    );
  }

  return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-red-800 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          {alertes.length} Alerte(s)
        </h3>
        <button onClick={onRefresh} className="text-red-600 hover:text-red-800">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
      <div className="space-y-2 max-h-60 overflow-y-auto">
        {alertes.map((a, idx) => (
          <div key={idx} className={`p-2 rounded-lg text-sm ${
            a.type_alerte === 'PERIME' ? 'bg-red-100 text-red-800' :
            a.type_alerte === 'EPUISE' ? 'bg-gray-100 text-gray-800' :
            'bg-orange-100 text-orange-800'
          }`}>
            <span className="font-medium">{a.icone} {a.variete_nom}</span>
            <span className="mx-2">•</span>
            <span>{a.type_alerte === 'PERIME' ? 'Périmé' : 
                   a.type_alerte === 'EPUISE' ? 'Épuisé' :
                   a.type_alerte === 'PEREMPTION_PROCHE' ? `Expire dans ${a.jours_avant_peremption}j` :
                   a.type_alerte === 'STOCK_BAS' ? `Stock bas (${a.quantite_restante})` :
                   'Germination faible'}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL : Gestion Semences
// ═══════════════════════════════════════════════════════════════════════════
const GestionSemences = () => {
  // États
  const [lots, setLots] = useState([]);
  const [alertes, setAlertes] = useState([]);
  const [stats, setStats] = useState(null);
  const [varietes, setVarietes] = useState([]);
  const [fournisseurs, setFournisseurs] = useState([]);
  const [familles, setFamilles] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtreStatut, setFiltreStatut] = useState('tous');
  const [filtreFamille, setFiltreFamille] = useState('');
  
  const [showModalLot, setShowModalLot] = useState(false);
  const [lotEdition, setLotEdition] = useState(null);
  const [showModalMouvement, setShowModalMouvement] = useState(false);
  const [lotMouvement, setLotMouvement] = useState(null);
  const [typeMouvement, setTypeMouvement] = useState('sortie_semis');

  // Chargement initial
  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadLots(),
        loadAlertes(),
        loadStats(),
        loadVarietes(),
        loadFournisseurs(),
        loadFamilles()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadLots = async () => {
    try {
      const res = await fetch(`${API_BASE}/semences/lots`);
      const data = await res.json();
      if (data.success) setLots(data.data || []);
    } catch (e) {
      console.error('Erreur chargement lots:', e);
    }
  };

  const loadAlertes = async () => {
    try {
      const res = await fetch(`${API_BASE}/semences/alertes`);
      const data = await res.json();
      if (data.success) setAlertes(data.data || []);
    } catch (e) {
      console.error('Erreur chargement alertes:', e);
    }
  };

  const loadStats = async () => {
    try {
      const res = await fetch(`${API_BASE}/semences/stats`);
      const data = await res.json();
      if (data.success) setStats(data.stats);
    } catch (e) {
      console.error('Erreur chargement stats:', e);
    }
  };

  const loadVarietes = async () => {
    try {
      const res = await fetch(`${API_BASE}/semences/varietes`);
      const data = await res.json();
      if (data.success) setVarietes(data.data || []);
    } catch (e) {
      console.error('Erreur chargement varietes:', e);
    }
  };

  const loadFournisseurs = async () => {
    try {
      const res = await fetch(`${API_BASE}/semences/fournisseurs`);
      const data = await res.json();
      if (data.success) setFournisseurs(data.data || []);
    } catch (e) {
      console.error('Erreur chargement fournisseurs:', e);
    }
  };

  const loadFamilles = async () => {
    try {
      const res = await fetch(`${API_BASE}/semences/familles`);
      const data = await res.json();
      if (data.success) setFamilles(data.data || []);
    } catch (e) {
      console.error('Erreur chargement familles:', e);
    }
  };

  // Sauvegarde lot
  const handleSaveLot = async (formData) => {
    const url = lotEdition 
      ? `${API_BASE}/semences/lots/${lotEdition.lot_id}`
      : `${API_BASE}/semences/lots`;
    const method = lotEdition ? 'PUT' : 'POST';
    
    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData)
    });
    
    const data = await res.json();
    if (!data.success) throw new Error(data.error);
    
    await loadLots();
    await loadStats();
    await loadAlertes();
  };

  // Sauvegarde mouvement
  const handleSaveMouvement = async (formData) => {
    const res = await fetch(`${API_BASE}/semences/mouvements`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData)
    });
    
    const data = await res.json();
    if (!data.success) throw new Error(data.error);
    
    await loadLots();
    await loadStats();
    await loadAlertes();
  };

  // Filtrage
  const lotsFiltres = lots.filter(lot => {
    // Recherche
    if (search) {
      const s = search.toLowerCase();
      if (!lot.variete_nom?.toLowerCase().includes(s) &&
          !lot.code_lot?.toLowerCase().includes(s) &&
          !lot.fournisseur_nom?.toLowerCase().includes(s)) {
        return false;
      }
    }
    // Statut
    if (filtreStatut === 'stock_bas' && lot.statut_stock !== 'stock_bas' && lot.statut_stock !== 'epuise') return false;
    if (filtreStatut === 'peremption' && lot.statut_peremption !== 'perime' && lot.statut_peremption !== 'peremption_proche') return false;
    if (filtreStatut === 'ok' && (lot.statut_stock !== 'ok' || lot.statut_peremption !== 'ok')) return false;
    // Famille
    if (filtreFamille && lot.code_famille !== filtreFamille) return false;
    
    return true;
  });

  // Handlers
  const handleEdit = (lot) => {
    setLotEdition(lot);
    setShowModalLot(true);
  };

  const handleMouvement = (lot, type) => {
    setLotMouvement(lot);
    setTypeMouvement(type);
    setShowModalMouvement(true);
  };

  const handleVoirDetail = (lot) => {
    // TODO: Modal détail avec historique
    alert(`Historique lot ${lot.code_lot}\n\n(À implémenter)`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 text-green-600 animate-spin" />
        <span className="ml-3 text-gray-600">Chargement de la caverne...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 rounded-xl shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="bg-white/20 rounded-full p-3">
              <Package className="w-10 h-10" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">🌱 Caverne d'Ali Baba</h1>
              <p className="text-green-100">Gestion des semences et plants</p>
            </div>
          </div>
          <button
            onClick={() => { setLotEdition(null); setShowModalLot(true); }}
            className="flex items-center gap-2 bg-white text-green-600 px-5 py-3 rounded-lg font-semibold hover:bg-green-50 transition shadow-lg"
          >
            <Plus className="w-5 h-5" />
            Nouveau lot
          </button>
        </div>
        
        {/* Stats rapides */}
        {stats && (
          <div className="mt-4 grid grid-cols-4 gap-3">
            <div className="bg-white/20 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold">{stats.nb_lots || 0}</div>
              <div className="text-xs text-green-100">Lots</div>
            </div>
            <div className="bg-white/20 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold">{stats.nb_varietes || 0}</div>
              <div className="text-xs text-green-100">Variétés</div>
            </div>
            <div className="bg-white/20 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold">{stats.lots_alerte_stock || 0}</div>
              <div className="text-xs text-green-100">⚠️ Stock bas</div>
            </div>
            <div className="bg-white/20 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold">{stats.lots_alerte_peremption || 0}</div>
              <div className="text-xs text-green-100">⏰ Péremption</div>
            </div>
          </div>
        )}
      </div>

      {/* Alertes */}
      <PanneauAlertes alertes={alertes} onRefresh={loadAlertes} />

      {/* Filtres */}
      <div className="bg-white rounded-xl shadow p-4">
        <div className="flex flex-wrap gap-4 items-center">
          {/* Recherche */}
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher variété, lot, fournisseur..."
                className="w-full pl-10 pr-4 py-2 border rounded-lg"
              />
            </div>
          </div>
          
          {/* Filtre statut */}
          <select
            value={filtreStatut}
            onChange={(e) => setFiltreStatut(e.target.value)}
            className="p-2 border rounded-lg"
          >
            <option value="tous">Tous statuts</option>
            <option value="ok">✅ OK</option>
            <option value="stock_bas">⚠️ Stock bas / Épuisé</option>
            <option value="peremption">⏰ Péremption proche</option>
          </select>
          
          {/* Filtre famille */}
          <select
            value={filtreFamille}
            onChange={(e) => setFiltreFamille(e.target.value)}
            className="p-2 border rounded-lg"
          >
            <option value="">Toutes familles</option>
            {familles.map(f => (
              <option key={f.id} value={f.code_famille}>{f.icone} {f.nom}</option>
            ))}
          </select>
          
          {/* Refresh */}
          <button
            onClick={loadAll}
            className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200"
            title="Rafraîchir"
          >
            <RefreshCw className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Liste des lots */}
      {lotsFiltres.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-8 text-center">
          <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Aucun lot trouvé</p>
          <button
            onClick={() => { setLotEdition(null); setShowModalLot(true); }}
            className="mt-4 text-green-600 hover:underline"
          >
            + Ajouter un premier lot
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {lotsFiltres.map(lot => (
            <CarteLot
              key={lot.lot_id}
              lot={lot}
              onEdit={handleEdit}
              onVoirDetail={handleVoirDetail}
              onMouvement={handleMouvement}
            />
          ))}
        </div>
      )}

      {/* Compteur */}
      <div className="text-center text-sm text-gray-500">
        {lotsFiltres.length} lot(s) affiché(s) sur {lots.length}
      </div>

      {/* Modal Lot */}
      {showModalLot && (
        <ModalLot
          lot={lotEdition}
          varietes={varietes}
          fournisseurs={fournisseurs}
          onSave={handleSaveLot}
          onClose={() => { setShowModalLot(false); setLotEdition(null); }}
        />
      )}

      {/* Modal Mouvement */}
      {showModalMouvement && lotMouvement && (
        <ModalMouvement
          lot={lotMouvement}
          type={typeMouvement}
          onSave={handleSaveMouvement}
          onClose={() => { setShowModalMouvement(false); setLotMouvement(null); }}
        />
      )}
    </div>
  );
};

export default GestionSemences;
